import { useCallback, useEffect, useRef, useState } from "react";
import {
  Check,
  Copy,
  PaperPlaneTilt,
  WhatsappLogo,
} from "@phosphor-icons/react";
import LoaderLoop from "./loader/LoaderLoop";
import { useToast } from "../../contexts/ToastContext";
import "./ShareLinkActions.css";

const COPIED_RESET_MS = 2000;

/**
 * Normalize an Israeli phone number to international digits for wa.me.
 * Accepts "+972XXXXXXXXX", "972XXXXXXXXX", "05XXXXXXXX", "05X-XXX-XXXX".
 * Returns null when the number cannot be normalized to something plausible.
 */
export function normalizeToWhatsappNumber(rawPhone) {
  if (!rawPhone || typeof rawPhone !== "string") return null;

  const digits = rawPhone.replace(/\D/g, "");
  if (!digits) return null;

  // Local Israeli format: 0XXXXXXXXX (9-10 digits incl. the leading zero)
  if (digits.startsWith("0")) {
    const withoutZero = digits.slice(1);
    if (withoutZero.length < 8 || withoutZero.length > 9) return null;
    return `972${withoutZero}`;
  }

  // Already international-ish (972... or any other country code)
  if (digits.length >= 10 && digits.length <= 15) return digits;

  return null;
}

/**
 * ShareLinkActions — shared row of actions for a patient-facing link:
 * copy link, send by SMS (parent-owned), share on WhatsApp.
 * Presentational only: no data fetching, no Redux, no firebase.
 */
function ShareLinkActions({
  url,
  phone = "",
  onSendSms = null,
  smsLabel = "",
  message = "",
  disabled = false,
  size = "md",
  className = "",
}) {
  const { showSuccess, showError } = useToast();
  const [copied, setCopied] = useState(false);
  const [smsPending, setSmsPending] = useState(false);
  const copyTimerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
        copyTimerRef.current = null;
      }
    };
  }, []);

  const legacyCopy = useCallback((value) => {
    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-1000px";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } finally {
      document.body.removeChild(textarea);
    }
    return ok;
  }, []);

  const handleCopy = useCallback(async () => {
    if (disabled || !url) return;

    let succeeded = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        succeeded = true;
      } else {
        succeeded = legacyCopy(url);
      }
    } catch (error) {
      // Clipboard API rejects on insecure origins / permission denial
      console.error("[CK-10170] Clipboard API copy failed, using fallback", {
        errorCode: "CK-10170",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      try {
        succeeded = legacyCopy(url);
      } catch (fallbackError) {
        console.error("[CK-10171] Fallback copy failed", {
          errorCode: "CK-10171",
          errorMessage: fallbackError?.message,
          timestamp: new Date().toISOString(),
        });
        succeeded = false;
      }
    }

    if (!succeeded) {
      showError("לא הצלחנו להעתיק את הקישור. נסו להעתיק ידנית");
      return;
    }

    showSuccess("הקישור הועתק");
    if (!mountedRef.current) return;
    setCopied(true);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => {
      if (mountedRef.current) setCopied(false);
    }, COPIED_RESET_MS);
  }, [disabled, url, legacyCopy, showError, showSuccess]);

  const handleSms = useCallback(async () => {
    if (disabled || !onSendSms || smsPending) return;
    setSmsPending(true);
    try {
      // Parent owns success/error toasts — it knows what was sent.
      await onSendSms();
    } catch (error) {
      console.error("[CK-10172] SMS send action rejected", {
        errorCode: "CK-10172",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
    } finally {
      if (mountedRef.current) setSmsPending(false);
    }
  }, [disabled, onSendSms, smsPending]);

  const whatsappNumber = normalizeToWhatsappNumber(phone);

  const handleWhatsapp = useCallback(() => {
    if (disabled || !whatsappNumber || !url) return;
    try {
      const text = message ? `${message}\n${url}` : url;
      const waUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
        text
      )}`;
      window.open(waUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("[CK-10173] Failed to open WhatsApp share window", {
        errorCode: "CK-10173",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError("לא הצלחנו לפתוח את וואטסאפ");
    }
  }, [disabled, whatsappNumber, url, message, showError]);

  if (!url) return null;

  const rootClass = [
    "sla-actions",
    size === "sm" ? "sla-sm" : "sla-md",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const iconSize = size === "sm" ? 13 : 14;

  return (
    <div className={rootClass}>
      <button
        type="button"
        className={`sla-btn sla-copy${copied ? " sla-copied" : ""}`}
        onClick={handleCopy}
        disabled={disabled}
        aria-label="העתקת הקישור"
      >
        {copied ? (
          <Check size={iconSize} weight="fill" />
        ) : (
          <Copy size={iconSize} weight="fill" />
        )}
        <span className="sla-btn-label">{copied ? "הועתק" : "העתקת קישור"}</span>
      </button>

      {onSendSms && (
        <button
          type="button"
          className="sla-btn sla-sms"
          onClick={handleSms}
          disabled={disabled || smsPending}
          aria-label={smsLabel || "שליחה ב-SMS"}
        >
          {smsPending ? (
            <LoaderLoop size="xs" />
          ) : (
            <PaperPlaneTilt size={iconSize} weight="fill" />
          )}
          <span className="sla-btn-label">
            {smsPending ? "שולח..." : smsLabel || "שליחה ב-SMS"}
          </span>
        </button>
      )}

      {whatsappNumber && (
        <button
          type="button"
          className="sla-btn sla-whatsapp"
          onClick={handleWhatsapp}
          disabled={disabled}
          aria-label="שיתוף בוואטסאפ"
        >
          <WhatsappLogo size={iconSize} weight="fill" />
          <span className="sla-btn-label">שיתוף בוואטסאפ</span>
        </button>
      )}
    </div>
  );
}

export default ShareLinkActions;
